import { useEffect, useState } from 'react';
import { PromoteAccountSuccessView } from './PromoteAccountSuccessView';
import { useCreateAccountTenant, usePromoteAccount } from '@/hooks/usePromoteAccount';
import { usePromoteLead } from '@/hooks/usePromoteLead';
import { usePromotions } from '@/hooks/usePromotions';
import { useAccountTenantUsers } from '@/hooks/useTenantSubscription';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { EntitlementOverrideEditor } from './EntitlementOverrideEditor';
import type {
  BillingMode,
  EnterpriseOverrides,
  Promotion,
  PlanName,
  PromoteAccountRequest,
} from '@/types/promote';

type PromoteAccountDialogProps = {
  accountId: string;
  accountName: string;
  primaryContact?: {
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  };
  open: boolean;
  onClose: () => void;
  targetType?: 'account' | 'lead';
  mode?: 'promote' | 'create_tenant';
};

const EMPTY_ENTERPRISE_OVERRIDES: EnterpriseOverrides = {
  feature_overrides: {},
  limit_overrides: {},
  custom_amount_cents: null,
  contract_start: null,
  contract_end: null,
  contract_notes: null,
};

const PLAN_OPTIONS = [
  { id: 'free', label: 'Free' },
  { id: 'starter', label: 'Starter' },
  { id: 'pro', label: 'Pro' },
  { id: 'enterprise', label: 'Enterprise' },
];

const BILLING_MODE_OPTIONS = [
  { id: 'invoice', label: 'Invoice' },
  { id: 'card', label: 'Card' },
];

function summarizePromotion(promotion: Promotion) {
  if (promotion.kind === 'percent_discount' && promotion.discount_percent != null) {
    return `${promotion.discount_percent}% off`;
  }
  if (promotion.kind === 'fixed_discount' && promotion.discount_fixed_cents != null) {
    return `$${(promotion.discount_fixed_cents / 100).toFixed(2)} off`;
  }
  if (promotion.kind === 'trial_extension' && promotion.trial_extension_days != null) {
    return `+${promotion.trial_extension_days} trial days`;
  }
  return promotion.display_name;
}

export function PromoteAccountDialog({
  accountId,
  accountName,
  primaryContact,
  open,
  onClose,
  targetType = 'account',
  mode = 'promote',
}: PromoteAccountDialogProps) {
  const promoteAccount = usePromoteAccount(accountId);
  const createAccountTenant = useCreateAccountTenant(accountId);
  const promoteLead = usePromoteLead(accountId);
  const promote = targetType === 'lead' ? promoteLead : mode === 'create_tenant' ? createAccountTenant : promoteAccount;
  const promotions = usePromotions({ isActive: true });
  const tenantUsers = useAccountTenantUsers(accountId, mode === 'create_tenant' && open);
  const [planName, setPlanName] = useState<PlanName>('starter');
  const [billingMode, setBillingMode] = useState<BillingMode>('invoice');
  const [adminSelectionMode, setAdminSelectionMode] = useState<'existing' | 'invite'>('invite');
  const [adminUserId, setAdminUserId] = useState('');
  const [adminEmail, setAdminEmail] = useState(primaryContact?.email ?? '');
  const [adminFirstName, setAdminFirstName] = useState(primaryContact?.first_name ?? '');
  const [adminLastName, setAdminLastName] = useState(primaryContact?.last_name ?? '');
  const [tenantName, setTenantName] = useState(accountName);
  const [promotionCode, setPromotionCode] = useState('');
  const [trialEndDate, setTrialEndDate] = useState('');
  const [trialNote, setTrialNote] = useState('');
  const [enterpriseOverrides, setEnterpriseOverrides] = useState<EnterpriseOverrides>(
    EMPTY_ENTERPRISE_OVERRIDES,
  );
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    promote.reset();
    setPlanName('starter');
    setBillingMode('invoice');
    setAdminSelectionMode('invite');
    setAdminUserId('');
    setAdminEmail(primaryContact?.email ?? '');
    setAdminFirstName(primaryContact?.first_name ?? '');
    setAdminLastName(primaryContact?.last_name ?? '');
    setTenantName(accountName);
    setPromotionCode('');
    setTrialEndDate('');
    setTrialNote('');
    setEnterpriseOverrides({
      ...EMPTY_ENTERPRISE_OVERRIDES,
      feature_overrides: {},
      limit_overrides: {},
    });
    setFormError(null);
  }, [accountName, open, primaryContact?.email, primaryContact?.first_name, primaryContact?.last_name]);

  if (!open) {
    return null;
  }

  async function submit() {
    setFormError(null);
    if (trialEndDate && !trialNote.trim()) {
      setFormError('Add a reason for the trial override.');
      return;
    }

    const useExistingAdmin = mode === 'create_tenant' && adminSelectionMode === 'existing' && adminUserId;
    const body: PromoteAccountRequest = {
      plan_name: planName,
      tenant_name: tenantName.trim() || undefined,
      promotion_code: promotionCode.trim() || undefined,
      billing_mode: billingMode,
      trial_end_override: trialEndDate ? new Date(`${trialEndDate}T12:00:00Z`).toISOString() : undefined,
      trial_source_note: trialNote.trim() || undefined,
      enterprise_overrides: planName === 'enterprise' ? enterpriseOverrides : undefined,
    };
    if (useExistingAdmin) {
      body.admin_user_id = adminUserId;
    } else {
      body.admin_email = adminEmail.trim();
      body.admin_first_name = adminFirstName.trim();
      body.admin_last_name = adminLastName.trim();
    }

    await promote.mutateAsync(body);
  }

  const isEnterprise = planName === 'enterprise';
  const mutationError = promote.error instanceof Error ? promote.error.message : null;
  const tenantUserOptions = tenantUsers.data?.items ?? [];
  const usingExistingAdmin = mode === 'create_tenant' && adminSelectionMode === 'existing';
  const adminReady = usingExistingAdmin
    ? Boolean(adminUserId)
    : Boolean(adminEmail && adminFirstName && adminLastName);

  return (
    <div className="apollo-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="apollo-modal"
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'create_tenant' ? `Create tenant for ${accountName}` : `Promote ${accountName} to customer`}
        onClick={(event) => event.stopPropagation()}
      >
        {promote.isSuccess ? (
          <PromoteAccountSuccessView
            inviteUrl={promote.data.invite_url}
            checkoutUrl={promote.data.checkout_url ?? null}
            accountId={accountId}
            onClose={onClose}
          />
        ) : (
          <div className="detail-stack">
            <div>
              <p className="entity-breadcrumb">{mode === 'create_tenant' ? 'Tenant' : 'Promotion'}</p>
              <h2>{mode === 'create_tenant' ? `Create tenant for ${accountName}` : `Promote ${accountName} to customer`}</h2>
            </div>

            <section className="form-section">
              <h3>Plan & Pricing</h3>
              <div className="form-grid">
                <label className="form-field">
                  <span>Plan</span>
                  <SearchableSelect
                    options={PLAN_OPTIONS}
                    value={planName}
                    onChange={(value) => setPlanName(value as PlanName)}
                    placeholder="Select plan"
                    clearable={false}
                  />
                </label>
                <label className="form-field">
                  <span>Billing mode</span>
                  <SearchableSelect
                    options={BILLING_MODE_OPTIONS}
                    value={billingMode}
                    onChange={(value) => setBillingMode(value as BillingMode)}
                    placeholder="Billing mode"
                    clearable={false}
                  />
                </label>
                <label className="form-field">
                  <span>Promotion code</span>
                  <SearchableSelect
                    options={[
                      { id: '', label: promotions.isLoading ? 'Loading promotions...' : 'No promotion' },
                      ...(promotions.data ?? []).map((promotion) => ({
                        id: promotion.code,
                        label: promotion.code,
                        description: summarizePromotion(promotion),
                      })),
                    ]}
                    value={promotionCode}
                    onChange={setPromotionCode}
                    disabled={promotions.isLoading}
                    isLoading={promotions.isLoading}
                    placeholder="No promotion"
                    clearable={false}
                  />
                </label>
                <label className="form-field">
                  <span>Trial end override</span>
                  <input type="date" value={trialEndDate} onChange={(event) => setTrialEndDate(event.target.value)} />
                </label>
                <label className="form-field form-field-wide">
                  <span>Trial override reason</span>
                  <textarea value={trialNote} onChange={(event) => setTrialNote(event.target.value)} rows={3} />
                </label>
              </div>
            </section>

            {isEnterprise ? (
              <section className="form-section">
                <h3>Enterprise Overrides</h3>
                <div className="form-grid">
                  <label className="form-field">
                    <span>Custom monthly amount (cents)</span>
                    <input
                      type="number"
                      value={enterpriseOverrides.custom_amount_cents ?? ''}
                      onChange={(event) =>
                        setEnterpriseOverrides((current) => ({
                          ...current,
                          custom_amount_cents: event.target.value ? Number(event.target.value) : null,
                        }))
                      }
                    />
                  </label>
                  <label className="form-field">
                    <span>Contract start</span>
                    <input
                      type="date"
                      value={enterpriseOverrides.contract_start ?? ''}
                      onChange={(event) =>
                        setEnterpriseOverrides((current) => ({
                          ...current,
                          contract_start: event.target.value || null,
                        }))
                      }
                    />
                  </label>
                  <label className="form-field">
                    <span>Contract end</span>
                    <input
                      type="date"
                      value={enterpriseOverrides.contract_end ?? ''}
                      onChange={(event) =>
                        setEnterpriseOverrides((current) => ({
                          ...current,
                          contract_end: event.target.value || null,
                        }))
                      }
                    />
                  </label>
                  <label className="form-field form-field-wide">
                    <span>Contract notes</span>
                    <textarea
                      rows={3}
                      value={enterpriseOverrides.contract_notes ?? ''}
                      onChange={(event) =>
                        setEnterpriseOverrides((current) => ({
                          ...current,
                          contract_notes: event.target.value || null,
                        }))
                      }
                    />
                  </label>
                  <div className="form-field-wide">
                    <EntitlementOverrideEditor
                      featureOverrides={enterpriseOverrides.feature_overrides}
                      limitOverrides={enterpriseOverrides.limit_overrides}
                      onFeatureOverridesChange={(feature_overrides) =>
                        setEnterpriseOverrides((current) => ({ ...current, feature_overrides }))
                      }
                      onLimitOverridesChange={(limit_overrides) =>
                        setEnterpriseOverrides((current) => ({ ...current, limit_overrides }))
                      }
                    />
                  </div>
                </div>
              </section>
            ) : null}

            <section className="form-section">
              <h3>Admin User</h3>
              {mode === 'create_tenant' && tenantUserOptions.length ? (
                <div className="segmented-control">
                  <button
                    type="button"
                    className={adminSelectionMode === 'existing' ? 'active' : ''}
                    onClick={() => setAdminSelectionMode('existing')}
                  >
                    Existing user
                  </button>
                  <button
                    type="button"
                    className={adminSelectionMode === 'invite' ? 'active' : ''}
                    onClick={() => setAdminSelectionMode('invite')}
                  >
                    Invite new
                  </button>
                </div>
              ) : null}
              <div className="form-grid">
                {usingExistingAdmin ? (
                  <label className="form-field form-field-wide">
                    <span>Existing account user</span>
                    <SearchableSelect
                      options={[
                        { id: '', label: tenantUsers.isLoading ? 'Loading users...' : 'Select a user' },
                        ...tenantUserOptions.map((user) => ({
                          id: user.id,
                          label: `${user.first_name} ${user.last_name}`.trim() || user.email,
                          description: user.email,
                        })),
                      ]}
                      value={adminUserId}
                      onChange={setAdminUserId}
                      disabled={tenantUsers.isLoading}
                      isLoading={tenantUsers.isLoading}
                      placeholder="Select a user"
                      clearable={false}
                    />
                  </label>
                ) : (
                  <>
                    <label className="form-field">
                      <span>First name</span>
                      <input value={adminFirstName} onChange={(event) => setAdminFirstName(event.target.value)} />
                    </label>
                    <label className="form-field">
                      <span>Last name</span>
                      <input value={adminLastName} onChange={(event) => setAdminLastName(event.target.value)} />
                    </label>
                    <label className="form-field">
                      <span>Email</span>
                      <input type="email" value={adminEmail} onChange={(event) => setAdminEmail(event.target.value)} />
                    </label>
                  </>
                )}
                <label className="form-field">
                  <span>Tenant name</span>
                  <input value={tenantName} onChange={(event) => setTenantName(event.target.value)} />
                </label>
              </div>
            </section>

            {formError || mutationError ? (
              <div className="form-error">{formError || mutationError}</div>
            ) : null}

            <div className="entity-header-actions">
              <button type="button" className="button button-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={submit}
                disabled={promote.isPending || !adminReady}
              >
                {promote.isPending ? 'Creating...' : mode === 'create_tenant' ? 'Create tenant' : 'Create customer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
